/**
 * API client for the ARIES backend.
 *
 * Every network call in the app goes through here. Components must not call
 * `fetch` directly — that keeps auth, error handling, and response typing in
 * one place.
 */

import type {
  AlertMine,
  AlertRespondRequest,
  ApiErrorBody,
  Customer,
  CustomerRequestRow,
  ExportRequest,
  FinanceConsole,
  ForecastMonthly,
  ForecastSummary,
  AccessLogPage,
  AlertRule,
  BulkEditPreviewResponse,
  BulkEditRequest,
  BulkReassignRequest,
  ClosedMonth,
  FinanceOwnerAccount,
  CustomFieldDefinition,
  ForecastView,
  ForecastViewCreate,
  ForecastViewUpdate,
  ForecastYear,
  ForecastYearCreate,
  LoginRequest,
  LookupCreate,
  LookupUpdate,
  LookupValue,
  Opportunity,
  OpportunityCreate,
  OpportunityPage,
  OpportunityPreviewRequest,
  OpportunityPreviewResponse,
  OpportunityUpdate,
  EmailDeliveryRow,
  ForecastSnapshot,
  PublishedCurrent,
  User,
  UserPage,
  UserRole,
  WeeklySummary,
  HygieneChecksConfig,
  AiAskCatalogueResponse,
  AiCombinedPreview,
  AiFinanceSettings,
  AiProposalCard,
  AiSystemSettings,
  AiSystemSettingsPatch,
  AiInteractionSummary,
  AiTurnSummary,
  ChatTurnResponse,
  PublishRequest,
  PublishResponse,
  RestoreBaselineResponse,
  RevenueRuleActivateResult,
  RevenueRuleDefinition,
  RevenueRulePreview,
  RevenueRuleVersionSummary,
  Stage,
  StageCreate,
  StageUpdate,
  ConfigImportSummary,
  TokenResponse,
} from './types'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '')

const TOKEN_KEY = 'aries.access_token'

/** A failed API call, carrying the server's error envelope. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error?.message ?? fallback)
    this.name = 'ApiError'
    this.status = status
    this.code = body?.error?.code ?? 'error.unknown'
    this.details = body?.error?.details ?? {}
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null): void {
  if (token === null) {
    localStorage.removeItem(TOKEN_KEY)
  } else {
    localStorage.setItem(TOKEN_KEY, token)
  }
}

type QueryPrimitive = string | number | boolean
type QueryValue = QueryPrimitive | QueryPrimitive[] | undefined | null

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, QueryValue>
  auth?: boolean
}

/** Build a URL from a path and a query object. Exported for testing. */
export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`
  if (!query) return url

  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item !== undefined && item !== null && item !== '') {
          params.append(key, String(item))
        }
      }
      continue
    }
    params.append(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, auth = true } = options

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const token = getToken()
  if (auth && token) {
    headers.Authorization = `Bearer ${token}`
  }

  const response = await fetch(buildUrl(path, query), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  const payload = contentType.includes('application/json')
    ? ((await response.json()) as unknown)
    : await response.text()

  if (!response.ok) {
    const errorBody =
      typeof payload === 'object' && payload !== null ? (payload as ApiErrorBody) : null
    throw new ApiError(response.status, errorBody, `Request failed with status ${response.status}`)
  }

  return payload as T
}

/* ------------------------------------------------------------------ */
/* Auth                                                                */
/* ------------------------------------------------------------------ */

export async function login(credentials: LoginRequest): Promise<TokenResponse> {
  const result = await request<TokenResponse>('/auth/login', {
    method: 'POST',
    body: credentials,
    auth: false,
  })
  setToken(result.access_token)
  return result
}

/** Exchange a Microsoft Entra ID token for an ARIES session JWT. */
export async function loginWithOidc(idToken: string): Promise<TokenResponse> {
  const result = await request<TokenResponse>('/auth/oidc', {
    method: 'POST',
    body: { id_token: idToken },
    auth: false,
  })
  setToken(result.access_token)
  return result
}

export async function logout(): Promise<void> {
  try {
    await request<void>('/auth/logout', { method: 'POST' })
  } catch {
    // A failed sign-out must not block the client from clearing its token.
  } finally {
    setToken(null)
  }
}

export function fetchCurrentUser(): Promise<User> {
  return request<User>('/auth/me')
}

/* ------------------------------------------------------------------ */
/* Config (read)                                                       */
/* ------------------------------------------------------------------ */

export function fetchStages(opts?: { includeRetired?: boolean }): Promise<Stage[]> {
  return request<Stage[]>('/config/stages', {
    query: { include_retired: opts?.includeRetired ? true : undefined },
  })
}

export function createStage(payload: StageCreate): Promise<Stage> {
  return request<Stage>('/config/stages', { method: 'POST', body: payload })
}

export function updateStage(id: string, payload: StageUpdate): Promise<Stage> {
  return request<Stage>(`/config/stages/${id}`, { method: 'PATCH', body: payload })
}

export function fetchForecastViews(): Promise<ForecastView[]> {
  return request<ForecastView[]>('/config/views')
}

export function createForecastView(payload: ForecastViewCreate): Promise<ForecastView> {
  return request<ForecastView>('/config/views', { method: 'POST', body: payload })
}

export function updateForecastView(id: string, payload: ForecastViewUpdate): Promise<ForecastView> {
  return request<ForecastView>(`/config/views/${id}`, { method: 'PATCH', body: payload })
}

export function fetchForecastYears(opts?: { openOnly?: boolean }): Promise<ForecastYear[]> {
  return request<ForecastYear[]>('/config/years', {
    query: { open_only: opts?.openOnly === false ? false : undefined },
  })
}

export function openForecastYear(payload: ForecastYearCreate): Promise<ForecastYear> {
  return request<ForecastYear>('/config/years', { method: 'POST', body: payload })
}

export function updateForecastYear(
  id: string,
  payload: { is_open: boolean },
): Promise<ForecastYear> {
  return request<ForecastYear>(`/config/years/${id}`, { method: 'PATCH', body: payload })
}

export function fetchPublicSettings(): Promise<{ settings: Record<string, unknown> }> {
  return request<{ settings: Record<string, unknown> }>('/config/settings')
}

export function patchPublicSettings(
  settings: Record<string, unknown>,
): Promise<{ settings: Record<string, unknown> }> {
  return request<{ settings: Record<string, unknown> }>('/config/settings', {
    method: 'PATCH',
    body: { settings },
  })
}

export function fetchSystemSettings(): Promise<{ settings: Record<string, unknown> }> {
  return request<{ settings: Record<string, unknown> }>('/config/system-settings')
}

export function patchSystemSettings(
  settings: Record<string, unknown>,
): Promise<{ settings: Record<string, unknown> }> {
  return request<{ settings: Record<string, unknown> }>('/config/system-settings', {
    method: 'PATCH',
    body: { settings },
  })
}

export function fetchFeatureSwitches(): Promise<{ feature_switches: Record<string, boolean> }> {
  return request<{ feature_switches: Record<string, boolean> }>('/config/feature-switches')
}

export function patchFeatureSwitches(
  feature_switches: Record<string, boolean>,
): Promise<{ feature_switches: Record<string, boolean> }> {
  return request<{ feature_switches: Record<string, boolean> }>('/config/feature-switches', {
    method: 'PATCH',
    body: { feature_switches },
  })
}

export function fetchRevenueRules(): Promise<RevenueRuleDefinition> {
  return request<RevenueRuleDefinition>('/config/revenue-rules/active')
}

export function fetchRevenueRuleVersions(): Promise<RevenueRuleVersionSummary[]> {
  return request<RevenueRuleVersionSummary[]>('/config/revenue-rules')
}

export function previewRevenueRule(payload: {
  payload: Record<string, unknown>
  effective_from: string
}): Promise<RevenueRulePreview> {
  return request<RevenueRulePreview>('/config/revenue-rules/preview', {
    method: 'POST',
    body: payload,
  })
}

export function activateRevenueRule(payload: {
  payload: Record<string, unknown>
  effective_from: string
  closed_month_reason?: string
}): Promise<RevenueRuleActivateResult> {
  return request<RevenueRuleActivateResult>('/config/revenue-rules/activate', {
    method: 'POST',
    body: payload,
  })
}

export function revertRevenueRule(
  version: number,
  payload?: { effective_from?: string; closed_month_reason?: string },
): Promise<RevenueRuleActivateResult> {
  return request<RevenueRuleActivateResult>(`/config/revenue-rules/${version}/revert`, {
    method: 'POST',
    body: payload ?? {},
  })
}

export function fetchForecastDrivers(): Promise<LookupValue[]> {
  return request<LookupValue[]>('/config/lookups', { query: { list_name: 'forecast_driver' } })
}

export function createLookup(payload: LookupCreate): Promise<LookupValue> {
  return request<LookupValue>('/config/lookups', { method: 'POST', body: payload })
}

export function updateLookup(id: string, payload: LookupUpdate): Promise<LookupValue> {
  return request<LookupValue>(`/config/lookups/${id}`, { method: 'PATCH', body: payload })
}

export function fetchAlertRules(): Promise<AlertRule[]> {
  return request<AlertRule[]>('/config/alert-rules')
}

export function updateAlertRule(id: string, payload: Record<string, unknown>): Promise<AlertRule> {
  return request<AlertRule>(`/config/alert-rules/${id}`, {
    method: 'PATCH',
    body: { payload },
  })
}

export function fetchClosedMonths(year?: number): Promise<ClosedMonth[]> {
  return request<ClosedMonth[]>('/config/closed-months', { query: { year } })
}

export function closeMonth(year: number, month: number): Promise<ClosedMonth> {
  return request<ClosedMonth>('/config/closed-months', {
    method: 'POST',
    body: { year, month },
  })
}

export function reopenMonth(id: string): Promise<void> {
  return request<void>(`/config/closed-months/${id}`, { method: 'DELETE' })
}

export function fetchCustomFields(): Promise<{ fields: CustomFieldDefinition[] }> {
  return request<{ fields: CustomFieldDefinition[] }>('/config/custom-fields')
}

export function putCustomFields(
  fields: CustomFieldDefinition[],
): Promise<{ fields: CustomFieldDefinition[] }> {
  return request<{ fields: CustomFieldDefinition[] }>('/config/custom-fields', {
    method: 'PUT',
    body: { fields },
  })
}

export function fetchHygieneChecksConfig(): Promise<HygieneChecksConfig> {
  return request<HygieneChecksConfig>('/config/hygiene-checks')
}

export function patchHygieneChecksConfig(
  body: Partial<HygieneChecksConfig['settings']>,
): Promise<HygieneChecksConfig> {
  return request<HygieneChecksConfig>('/config/hygiene-checks', {
    method: 'PATCH',
    body,
  })
}

export function fetchAiSystemSettings(): Promise<AiSystemSettings> {
  return request<AiSystemSettings>('/ai/settings/system')
}

export function patchAiSystemSettings(body: AiSystemSettingsPatch): Promise<AiSystemSettings> {
  return request<AiSystemSettings>('/ai/settings/system', {
    method: 'PATCH',
    body,
  })
}

export function fetchAiFinanceSettings(): Promise<AiFinanceSettings> {
  return request<AiFinanceSettings>('/ai/settings/finance')
}

export function patchAiFinanceSettings(
  body: Partial<AiFinanceSettings>,
): Promise<AiFinanceSettings> {
  return request<AiFinanceSettings>('/ai/settings/finance', {
    method: 'PATCH',
    body,
  })
}

export function createAiSession(): Promise<{ id: string }> {
  return request<{ id: string }>('/ai/sessions', { method: 'POST', body: {} })
}

export function closeAiSession(sessionId: string): Promise<{ id: string }> {
  return request<{ id: string }>(`/ai/sessions/${sessionId}/close`, { method: 'POST' })
}

export function postChatTurn(body: {
  session_id?: string | null
  message: string
  attachment_text?: string | null
  mode: 'mutate' | 'ask'
  pasted_ranges?: Array<{ start: number; end: number }>
}): Promise<ChatTurnResponse> {
  return request<ChatTurnResponse>('/ai/chat/turns', { method: 'POST', body })
}

export function postAiAsk(body: {
  query_id?: string
  params?: Record<string, unknown>
  message?: string
  session_id?: string | null
}): Promise<ChatTurnResponse & { answer?: string }> {
  return request<ChatTurnResponse & { answer?: string }>('/ai/ask', { method: 'POST', body })
}

export function fetchAiAskCatalogue(): Promise<AiAskCatalogueResponse> {
  return request<AiAskCatalogueResponse>('/ai/ask/catalogue')
}

export function decideAiBatch(
  batchId: string,
  decisions: Array<{
    proposal_id: string
    decision: 'accept' | 'skip'
    op_acks?: string[]
  }>,
): Promise<{ id: string; soft_limit?: number; hard_cap?: number; cards: AiProposalCard[] }> {
  return request<{ id: string; soft_limit?: number; hard_cap?: number; cards: AiProposalCard[] }>(
    `/ai/batches/${batchId}/decide`,
    {
      method: 'POST',
      body: { decisions },
    },
  )
}

export function cancelAiProposal(proposalId: string): Promise<AiProposalCard> {
  return request<AiProposalCard>(`/ai/proposals/${proposalId}/cancel`, { method: 'POST' })
}

export function selectAiProposalOpportunity(
  proposalId: string,
  opportunityId: string,
): Promise<AiProposalCard> {
  return request<AiProposalCard>(`/ai/proposals/${proposalId}/select-opportunity`, {
    method: 'POST',
    body: { opportunity_id: opportunityId },
  })
}

export function clarifyAiProposal(
  proposalId: string,
  body: { fields: Record<string, unknown>; checks_acknowledged?: string[] },
): Promise<AiProposalCard> {
  return request<AiProposalCard>(`/ai/proposals/${proposalId}/clarify`, {
    method: 'POST',
    body,
  })
}

export function undoAiProposal(proposalId: string): Promise<AiProposalCard> {
  return request<AiProposalCard>(`/ai/proposals/${proposalId}/undo`, { method: 'POST' })
}

export function fetchAiCombinedPreview(batchId: string): Promise<AiCombinedPreview> {
  return request<AiCombinedPreview>(`/ai/batches/${batchId}/combined-preview`)
}

export function fetchAiTurns(limit = 100): Promise<AiTurnSummary[]> {
  return request<AiTurnSummary[]>(`/ai/turns?limit=${limit}`)
}

export function fetchAiInteractions(limit = 100): Promise<AiInteractionSummary[]> {
  return request<AiInteractionSummary[]>(`/ai/interactions?limit=${limit}`)
}

export async function exportConfigurationSnapshot(): Promise<void> {
  const token = getToken()
  const headers: Record<string, string> = {
    Accept: 'application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(buildUrl('/config/export'), { method: 'GET', headers })

  if (!response.ok) {
    let errorBody: ApiErrorBody | null = null
    try {
      errorBody = (await response.json()) as ApiErrorBody
    } catch {
      /* ignore */
    }
    throw new ApiError(response.status, errorBody, `Export failed with status ${response.status}`)
  }

  const disposition = response.headers.get('Content-Disposition') ?? ''
  const match = /filename="([^"]+)"/.exec(disposition)
  const filename = match?.[1] ?? 'aries-config.json'
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export function importConfigurationSnapshot(
  snapshot: Record<string, unknown>,
): Promise<ConfigImportSummary> {
  return request<ConfigImportSummary>('/config/import', {
    method: 'POST',
    body: snapshot,
  })
}

export function fetchCustomers(): Promise<Customer[]> {
  return request<Customer[]>('/customers')
}

export function createCustomer(payload: { name: string }): Promise<Customer> {
  return request<Customer>('/customers', { method: 'POST', body: payload })
}

export function updateCustomer(id: string, payload: { name: string }): Promise<Customer> {
  return request<Customer>(`/customers/${id}`, { method: 'PATCH', body: payload })
}

export function fetchUsers(skip = 0, limit = 100): Promise<UserPage> {
  return request<UserPage>('/users', { query: { skip, limit } })
}

export function createUser(body: {
  email: string
  full_name: string
  password: string
  role: UserRole
  is_super_owner?: boolean
}): Promise<User> {
  return request<User>('/users', { method: 'POST', body })
}

export function patchUser(
  id: string,
  body: { role?: UserRole; is_active?: boolean; is_super_owner?: boolean },
): Promise<User> {
  return request<User>(`/users/${id}`, { method: 'PATCH', body })
}

/* ------------------------------------------------------------------ */
/* Opportunities                                                       */
/* ------------------------------------------------------------------ */

export interface OpportunityQuery {
  year?: number
  view?: string
  stage_id?: string
  owner_id?: string
  /** One or more customer ids (repeated `customer_id` query params). */
  customer_id?: string | string[]
  search?: string
  sort_by?: string
  sort_dir?: 'asc' | 'desc'
  /** When true, include lines with $0 unweighted year total (default hidden). */
  include_zero_year_total?: boolean
  /** When false, include soft-removed lines (restore workflow). */
  exclude_removed?: boolean
  skip?: number
  limit?: number
  /**
   * With sort_by=customer_year_total: pack whole customers into pages of at most
   * this many opportunity lines. skip is then the 0-based packed page index.
   */
  max_lines?: number
}

export function fetchOpportunities(query: OpportunityQuery = {}): Promise<OpportunityPage> {
  const q: RequestOptions['query'] = {
    year: query.year,
    view: query.view,
    stage_id: query.stage_id,
    owner_id: query.owner_id,
    customer_id: query.customer_id,
    search: query.search,
    sort_by: query.sort_by,
    sort_dir: query.sort_dir,
    include_zero_year_total: query.include_zero_year_total ? true : undefined,
    exclude_removed: query.exclude_removed === false ? false : undefined,
    skip: query.skip,
    limit: query.limit,
    max_lines: query.max_lines,
  }
  return request<OpportunityPage>('/opportunities', { query: q })
}

export function fetchOpportunity(id: string, year?: number): Promise<Opportunity> {
  return request<Opportunity>(`/opportunities/${id}`, { query: { year } })
}

export interface OpportunityAuditEvent {
  id: string
  action: string
  occurred_at: string
  actor_email: string | null
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  comment: string | null
  ai_assisted: boolean
  channel: string | null
}

export function fetchOpportunityAudit(id: string): Promise<OpportunityAuditEvent[]> {
  return request<OpportunityAuditEvent[]>(`/opportunities/${id}/audit`)
}

export function previewOpportunity(
  payload: OpportunityPreviewRequest,
  year?: number,
): Promise<OpportunityPreviewResponse> {
  return request<OpportunityPreviewResponse>('/opportunities/preview', {
    method: 'POST',
    body: payload,
    query: year !== undefined ? { year } : undefined,
  })
}

export function createOpportunity(payload: OpportunityCreate): Promise<Opportunity> {
  return request<Opportunity>('/opportunities', { method: 'POST', body: payload })
}

export function updateOpportunity(id: string, payload: OpportunityUpdate): Promise<Opportunity> {
  return request<Opportunity>(`/opportunities/${id}`, { method: 'PATCH', body: payload })
}

export function deleteOpportunity(id: string): Promise<void> {
  return request<void>(`/opportunities/${id}`, { method: 'DELETE' })
}

export function restoreOpportunity(id: string): Promise<Opportunity> {
  return request<Opportunity>(`/opportunities/${id}/restore`, { method: 'POST' })
}

export function bulkReassignOpportunities(payload: BulkReassignRequest): Promise<{
  updated_count: number
  opportunity_ids: string[]
}> {
  return request('/opportunities/bulk-reassign', { method: 'POST', body: payload })
}

export function previewBulkEdit(
  payload: BulkEditRequest,
  year?: number,
): Promise<BulkEditPreviewResponse> {
  return request<BulkEditPreviewResponse>('/opportunities/bulk-edit/preview', {
    method: 'POST',
    body: payload,
    query: year !== undefined ? { year } : undefined,
  })
}

export function applyBulkEdit(payload: BulkEditRequest): Promise<{
  updated_count: number
  opportunity_ids: string[]
}> {
  return request('/opportunities/bulk-edit', { method: 'POST', body: payload })
}

export function markOpportunityLost(id: string, version: number): Promise<Opportunity> {
  return request<Opportunity>(`/opportunities/${id}/mark-lost`, {
    method: 'POST',
    query: { expected_version: version },
  })
}

export function confirmOpportunityWeek(id: string): Promise<void> {
  return request<void>(`/opportunities/${id}/confirm-week`, { method: 'POST' })
}

/** Confirm the signed-in owner's forecast for the current ISO week. */
export function confirmOwnerWeek(): Promise<void> {
  return request<void>('/opportunities/confirm-week', { method: 'POST' })
}

/* ------------------------------------------------------------------ */
/* Forecast summary                                                    */
/* ------------------------------------------------------------------ */

export function fetchForecastSummary(year: number, view: string): Promise<ForecastSummary> {
  return request<ForecastSummary>('/forecast/summary', { query: { year, view } })
}

export function fetchForecastMonthly(year: number, view?: string): Promise<ForecastMonthly> {
  return request<ForecastMonthly>('/forecast/monthly', {
    query: { year, ...(view ? { view } : {}) },
  })
}

/* ------------------------------------------------------------------ */
/* Finance console, export, publish                                    */
/* ------------------------------------------------------------------ */

export function fetchFinanceConsole(): Promise<FinanceConsole> {
  return request<FinanceConsole>('/finance/console')
}

export async function exportForecastCsv(payload: ExportRequest): Promise<void> {
  const token = getToken()
  const headers: Record<string, string> = {
    Accept: 'text/csv, application/json',
    'Content-Type': 'application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(buildUrl('/finance/export'), {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    let errorBody: ApiErrorBody | null = null
    try {
      errorBody = (await response.json()) as ApiErrorBody
    } catch {
      /* ignore */
    }
    throw new ApiError(response.status, errorBody, `Export failed with status ${response.status}`)
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `aries-forecast-${payload.year}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export function publishForecast(payload: PublishRequest): Promise<PublishResponse> {
  return request<PublishResponse>('/finance/publish', { method: 'POST', body: payload })
}

export function fetchFinanceSnapshots(forecastYear?: number): Promise<ForecastSnapshot[]> {
  return request<ForecastSnapshot[]>('/finance/snapshots', {
    query: forecastYear !== undefined ? { forecast_year: forecastYear } : undefined,
  })
}

export function fetchPublishedCurrent(forecastYear?: number): Promise<PublishedCurrent> {
  return request<PublishedCurrent>('/finance/published/current', {
    query: forecastYear !== undefined ? { forecast_year: forecastYear } : undefined,
  })
}

export function fetchBaselineView(snapshotId: string): Promise<PublishedCurrent> {
  return request<PublishedCurrent>(`/finance/snapshots/${snapshotId}/view`)
}

export async function exportBaselineCsv(snapshotId: string, filenameHint?: string): Promise<void> {
  const token = getToken()
  const headers: Record<string, string> = {
    Accept: 'text/csv, application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(buildUrl(`/finance/snapshots/${snapshotId}/export`), {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    let errorBody: ApiErrorBody | null = null
    try {
      errorBody = (await response.json()) as ApiErrorBody
    } catch {
      /* ignore */
    }
    throw new ApiError(response.status, errorBody, `Export failed with status ${response.status}`)
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filenameHint ?? `aries-baseline-${snapshotId}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export function restoreBaseline(
  snapshotId: string,
  payload: { reason: string },
): Promise<RestoreBaselineResponse> {
  return request<RestoreBaselineResponse>(`/finance/snapshots/${snapshotId}/restore`, {
    method: 'POST',
    body: payload,
  })
}

export function softDeleteBaseline(snapshotId: string): Promise<ForecastSnapshot> {
  return request<ForecastSnapshot>(`/finance/snapshots/${snapshotId}`, { method: 'DELETE' })
}

export function fetchWeeklySummary(): Promise<WeeklySummary> {
  return request<WeeklySummary>('/finance/weekly-summary')
}

export interface ActualsImportResult {
  success_count: number
  failure_count: number
  failures: Array<{ line: number; reason: string; row: Record<string, string> }>
}

export function importActualsCsv(csv_text: string): Promise<ActualsImportResult> {
  return request<ActualsImportResult>('/finance/actuals/import', {
    method: 'POST',
    body: { csv_text },
  })
}

export async function downloadActualsTemplateCsv(year: number): Promise<void> {
  const token = getToken()
  const headers: Record<string, string> = {
    Accept: 'text/csv, application/json',
  }
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(buildUrl('/finance/actuals/template', { year }), {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    let errorBody: ApiErrorBody | null = null
    try {
      errorBody = (await response.json()) as ApiErrorBody
    } catch {
      /* ignore */
    }
    throw new ApiError(
      response.status,
      errorBody,
      `Actuals template download failed with status ${response.status}`,
    )
  }

  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `aries-actuals-template-${year}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export function fetchEmailFailures(): Promise<EmailDeliveryRow[]> {
  return request<EmailDeliveryRow[]>('/finance/email-failures')
}

export function retryEmailFailure(
  id: string,
): Promise<{ delivery: EmailDeliveryRow; retried: boolean }> {
  return request<{ delivery: EmailDeliveryRow; retried: boolean }>(
    `/finance/email-failures/${id}/retry`,
    { method: 'POST' },
  )
}

export function fetchFinanceOwners(): Promise<FinanceOwnerAccount[]> {
  return request<FinanceOwnerAccount[]>('/finance/owners')
}

export function fetchAccessLog(skip = 0, limit = 100): Promise<AccessLogPage> {
  return request<AccessLogPage>('/finance/access-log', { query: { skip, limit } })
}

export function approveCustomerRequest(id: string): Promise<Customer> {
  return request<Customer>(`/customer-requests/${id}/approve`, { method: 'POST' })
}

/* ------------------------------------------------------------------ */
/* Alerts                                                              */
/* ------------------------------------------------------------------ */

export function fetchMyAlerts(): Promise<AlertMine[]> {
  return request<AlertMine[]>('/alerts/mine')
}

export function respondToAlert(id: string, payload: AlertRespondRequest): Promise<void> {
  return request<void>(`/alerts/${id}/respond`, { method: 'POST', body: payload })
}

/** Alias for alert response actions (S-1A-032). */
export const respondAlert = respondToAlert

export type { CustomerRequestRow }
